import {useCallback, useEffect, useReducer, useState} from 'react';

import type {SettlementPaymentMethod} from '@afriex/core/domain/types';

import {invoiceService} from '@/services/invoiceService';
import {merchantService} from '@/services/merchants';
import {settlementAccountService} from '@/services/settlementAccountService';
import {AfriexCurrencyCode, isSettlementPaymentMethodArchived} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';
import {InvoiceValidator} from '@/validators/InvoiceValidator';

import type {InvoiceDraft} from './types';
import {InvoiceActionType} from './types';
import updateInvoiceReducer from './updateInvoiceReducer';

const emptyDraft = (businessId = ''): InvoiceDraft => ({
  businessId,
  description: '',
  amount: '',
  currency: AfriexCurrencyCode.USD,
  settlementAccountId: '',
});

/**
 * The create-invoice form.
 *
 * Three things live outside the reducer on purpose, because none of them is a
 * field the operator fills in: the resolved business name (the result of a
 * lookup), the settlement accounts available to choose from, and isLoading.
 */
const useInvoiceCreate = (
  onClose: VoidFunction,
  onSuccess?: () => void | Promise<void>,
  lockedBusinessId?: string,
) => {
  const [invoice, dispatch] = useReducer(
    updateInvoiceReducer,
    emptyDraft(lockedBusinessId),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Business id resolution
  const [businessName, setBusinessName] = useState<string | undefined>();
  const [isResolvingBusiness, setIsResolvingBusiness] = useState(false);
  const [businessNotFound, setBusinessNotFound] = useState(false);

  // Settlement accounts to pick from
  const [accounts, setAccounts] = useState<SettlementPaymentMethod[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // Archived accounts are excluded: they cannot be paid into. The list
        // endpoint has no deactivated filter, so it is applied here.
        const response =
          await settlementAccountService.getSettlementPaymentMethods({
            limit: 100,
          });
        if (!cancelled) {
          setAccounts(
            (response.data ?? []).filter(
              (method) => !isSettlementPaymentMethodArchived(method),
            ),
          );
        }
      } catch (e) {
        if (!cancelled) {
          showErrorMessage(
            `Could not load settlement accounts: ${getErrorMessage(e, String(e))}`,
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAccounts(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Resolves a typed business id to a name so the operator can see they have
   * the right merchant before raising anything against it. Best effort: a
   * failure shows "not found" rather than blocking the form.
   */
  const resolveBusiness = useCallback(async (businessId: string) => {
    const trimmed = businessId.trim();
    if (!trimmed) {
      setBusinessName(undefined);
      setBusinessNotFound(false);
      return;
    }
    setIsResolvingBusiness(true);
    setBusinessNotFound(false);
    try {
      const {business} = await merchantService.getBusinessDetails(trimmed);
      setBusinessName(business?.name);
      setBusinessNotFound(!business?.name);
    } catch {
      setBusinessName(undefined);
      setBusinessNotFound(true);
    } finally {
      setIsResolvingBusiness(false);
    }
  }, []);

  const handleSubmit = async (draft: InvoiceDraft) => {
    setIsSubmitting(true);
    try {
      const payload = new InvoiceValidator(draft).value();
      const created = await invoiceService.createInvoice(payload);
      showSuccessMessage(`Invoice ${created.meta.reference} created`);
      onClose();
      await onSuccess?.();
    } catch (e) {
      showErrorMessage(
        `Error creating invoice: ${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetDraft = useCallback(() => {
    dispatch({
      type: InvoiceActionType.RESET_INVOICE,
      payload: emptyDraft(lockedBusinessId),
    });
  }, [lockedBusinessId]);

  return {
    invoice,
    dispatch,
    handleSubmit,
    isSubmitting,
    resetDraft,
    businessName,
    isResolvingBusiness,
    businessNotFound,
    resolveBusiness,
    accounts,
    isLoadingAccounts,
  };
};

export default useInvoiceCreate;
